import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  getCannedResponses,
  createCannedResponse,
  updateCannedResponse,
  deleteCannedResponse,
  getTicketProjects,
  errorMessage,
} from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { PageHeading, Pill, NtIcon } from '../components/NotionUI';
import Select from '../components/Select';

const emptyForm = { title: '', body: '', project_id: '' };

function CannedResponses({ user, onLogout }) {
  const { t } = useLanguage();
  const [responses, setResponses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, proj] = await Promise.all([getCannedResponses(), getTicketProjects()]);
      setResponses(res.responses || []);
      setProjects(proj.projects || []);
    } catch (err) {
      setError(errorMessage(err, t('canned.loadError')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const startCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowForm(true);
    setError('');
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({ title: r.title, body: r.body, project_id: r.project_id ? String(r.project_id) : '' });
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = { title: form.title.trim(), body: form.body.trim(), project_id: form.project_id ? Number(form.project_id) : null };
    try {
      if (editId) await updateCannedResponse(editId, payload);
      else await createCannedResponse(payload);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('canned.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(t('canned.confirmDelete', { title: r.title }))) return;
    try {
      await deleteCannedResponse(r.id);
      await load();
    } catch (err) {
      setError(errorMessage(err, t('canned.deleteError')));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="chat" title={t('canned.title')} subtitle={t('canned.subtitle')} />

        {error && <div className="error-message">{error}</div>}

        {!showForm && (
          <div className="tp-toolbar">
            <span className="nt-muted">{loading ? '' : t('canned.count', { count: responses.length })}</span>
            <button className="btn" type="button" onClick={startCreate}>{t('canned.new')}</button>
          </div>
        )}

        {showForm && (
          <form className="tp-form" onSubmit={handleSubmit}>
            <section className="tp-section">
              <div className="tp-fields">
                <div className="form-group tp-full">
                  <label>{t('canned.fieldTitle')}</label>
                  <input value={form.title} maxLength={150} required onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('canned.fieldTitlePlaceholder')} />
                </div>
                <div className="form-group tp-full">
                  <label>{t('canned.fieldQueue')}</label>
                  <Select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                    <option value="">{t('canned.allQueues')}</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.code ? `${p.name} (${p.code})` : p.name}</option>
                    ))}
                  </Select>
                </div>
                <div className="form-group tp-full">
                  <label>{t('canned.fieldBody')}</label>
                  <textarea rows={8} value={form.body} required onChange={(e) => setForm({ ...form, body: e.target.value })} />
                  <p className="tp-hint">{t('canned.placeholdersHint')}</p>
                </div>
              </div>
            </section>
            <div className="tp-savebar">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn" disabled={saving}>{saving ? t('common.saving') : t('common.save')}</button>
            </div>
          </form>
        )}

        {!showForm && (loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : responses.length === 0 ? (
          <div className="au-empty">
            <span className="au-empty-icon" aria-hidden="true"><NtIcon name="chat" size={26} /></span>
            <p>{t('canned.empty')}</p>
            <button className="btn" type="button" onClick={startCreate}>{t('canned.new')}</button>
          </div>
        ) : (
          <div className="cn-list">
            {responses.map((r) => (
              <div key={r.id} className="cn-card">
                <div className="cn-head">
                  <h3 className="cn-title">{r.title}</h3>
                  {r.project_name ? <Pill>{r.project_name}</Pill> : <span className="nt-muted">{t('canned.allQueues')}</span>}
                  <span className="tp-spacer" />
                  <button className="tp-icon" type="button" onClick={() => startEdit(r)} title={t('common.edit')} aria-label={t('common.edit')}><NtIcon name="edit" size={16} /></button>
                  <button className="tp-icon tp-icon-danger" type="button" onClick={() => handleDelete(r)} title={t('common.delete')} aria-label={t('common.delete')}><NtIcon name="trash" size={16} /></button>
                </div>
                <p className="cn-body">{r.body}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Layout>
  );
}

export default CannedResponses;
